/**
 * Shared form components for modal wizards across the admin panel.
 */

export { Toggle } from "../ui/Toggle"

// ── CSS class helpers ──────────────────────────────────────────────────────────

export const inputCls =
  "h-11 w-full rounded-xl border border-gray-300 bg-white px-4 text-sm text-gray-800 focus:border-[var(--alas-blue)] focus:outline-none focus:ring-2 focus:ring-[var(--alas-blue)]/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white"

export const textareaCls =
  "w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-[var(--alas-blue)] focus:outline-none focus:ring-2 focus:ring-[var(--alas-blue)]/20 dark:border-gray-700 dark:bg-gray-900 dark:text-white"

export const labelCls = "mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400"

export const btnPrimary =
  "rounded-xl bg-[var(--alas-blue)] px-4 py-2 text-sm font-medium text-white hover:bg-[#173071] disabled:opacity-60"

export const btnSecondary =
  "rounded-xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-800"

export const btnSave =
  "rounded-xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-60"

// ── FormTabs (was WizardSteps) ─────────────────────────────────────────────────
// Tab bar for multi-section forms. Click any tab to jump directly — no forced order.

type FormTabsProps = {
  current: number
  labels: string[]
  onStep: (i: number) => void
}

export function WizardSteps({ current, labels, onStep }: FormTabsProps) {
  return (
    <div className="mb-5 flex gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
      {labels.map((label, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onStep(i)}
          className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${
            i === current
              ? "bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-white"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  )
}

// ── StyledSelect ───────────────────────────────────────────────────────────────

type StyledSelectProps = {
  value: string
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  disabled?: boolean
  id?: string
  ariaLabel?: string
  children: React.ReactNode
}

export function StyledSelect({ value, onChange, disabled, id, ariaLabel, children }: StyledSelectProps) {
  return (
    <div className="relative">
      <select
        id={id}
        aria-label={ariaLabel}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="h-11 w-full appearance-none rounded-xl border border-gray-300 bg-white px-4 pr-10 text-sm text-gray-800 focus:border-[var(--alas-blue)] focus:outline-none focus:ring-2 focus:ring-[var(--alas-blue)]/20 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-white"
      >
        {children}
      </select>
      <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    </div>
  )
}

// ── CheckRow ──────────────────────────────────────────────────────────────────
// Styled row for multi-select lists (courses, lessons, programs, etc.)

type CheckRowProps = {
  checked: boolean
  onChange: (v: boolean) => void
  label: string
  sublabel?: string
  badge?: string
}

export function CheckRow({ checked, onChange, label, sublabel, badge }: CheckRowProps) {
  return (
    <label className={`flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 transition-colors ${
      checked
        ? 'bg-brand-50 dark:bg-brand-900/20'
        : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'
    }`}>
      <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition-colors ${
        checked
          ? 'border-brand-500 bg-brand-500'
          : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-900'
      }`}>
        {checked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4l3 3 5-6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm text-gray-800 dark:text-gray-200">{label}</span>
        {sublabel && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{sublabel}</p>}
      </div>
      {badge && (
        <span className="text-[11px] rounded-full bg-gray-100 px-2 py-0.5 text-gray-500 dark:bg-gray-800 dark:text-gray-400 shrink-0">
          {badge}
        </span>
      )}
    </label>
  )
}

// ── WizardNav ─────────────────────────────────────────────────────────────────
// Standard bottom nav bar used in all wizard modals.

type WizardNavProps = {
  step: number
  maxStep: number
  onBack: () => void
  onNext: () => void
  saving?: boolean
  saveLabel?: string
  /** If true, submit button type is "submit". Default: true */
  submitOnSave?: boolean
}

export function WizardNav({
  step,
  maxStep,
  onBack,
  onNext,
  saving = false,
  saveLabel = "Guardar",
  submitOnSave = true,
}: WizardNavProps) {
  return (
    <div className="mt-5 flex items-center justify-between">
      <button
        type="button"
        onClick={onBack}
        className={`${btnSecondary} ${step === 0 ? "invisible" : ""}`}
      >
        ← Atrás
      </button>
      <div className="flex gap-2">
        <button
          type={submitOnSave ? "submit" : "button"}
          disabled={saving}
          className={btnSave}
        >
          {saving ? "Guardando..." : saveLabel}
        </button>
        {step < maxStep && (
          <button type="button" onClick={onNext} className={btnPrimary}>
            Siguiente →
          </button>
        )}
      </div>
    </div>
  )
}
